// src/stores/auth.js
import { defineStore } from 'pinia'
import { auth } from '@/firebase/init'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import type { User } from 'firebase/auth'
import router from '@/router'
import { useUserStore } from './userStore' // adjust the path if needed
import { useInviteStore } from '@/stores/inviteStore'
import { useLabStore } from '@/stores/labStore'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as User | null, // Firebase user or null
    ready: false, // becomes true after first onAuthStateChanged fires
  }),

  actions: {
    init(): Promise<void> {
      // avoid attaching multiple listeners
      if (this.ready) return Promise.resolve()

      return new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          this.user = user
          this.ready = true

          if (user) {
            const userStore = useUserStore()
            userStore.getCurrentUser(user.uid)
          }

          resolve() // let main.js continue
          unsubscribe() // optional: if you don't need realtime updates
          // If you want to stay synced to auth changes, remove unsubscribe()
        })
      })
    },

    updateUser(user: User | null) {
      this.user = user
    },

    async logout({ reason }: { reason?: string } = {}): Promise<void> {
      const userStore = useUserStore()
      const inviteStore = useInviteStore()
      const labStore = useLabStore()

      await signOut(auth)
      userStore.resetStore()
      inviteStore.resetStore()
      labStore.resetStore()
      this.user = null
      if (reason === 'deactivated') {
        const doReload = router.currentRoute.value.name === 'Home'
        await router.push({ name: 'Home', query: { reason: 'deactivated' } })

        if (doReload) {
          window.location.reload()
        }
        return
      }
      await router.push({ name: 'Home' })
    },
  },

  getters: {
    isLoggedIn: (state) => !!state.user,
  },
})

